import React, { useEffect, useRef, useState } from 'react';
import { Play, RotateCcw, Volume2, VolumeX, Sparkles, AudioWaveform, Gauge } from 'lucide-react';
import { audioEngine } from '../services/audioEngine';

interface AudioPlayerProps {
  isPlaying: boolean;
  isPlayingForward: boolean;
  isAudioReady: boolean;
  isWon: boolean;
  replaysCount: number;
  onPlayReverse: () => void;
  onPlayForward: () => void;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  isPlaying,
  isPlayingForward,
  isAudioReady,
  isWon,
  replaysCount,
  onPlayReverse,
  onPlayForward,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [speed, setSpeed] = useState(1.0);

  // Audio frequency waveform visualizer loop
  useEffect(() => {
    let animId: number;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = 32;
    const dataArray = new Uint8Array(bufferLength);

    const renderWave = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (isPlaying || isPlayingForward) {
        audioEngine.getFrequencyData(dataArray);
      } else {
        const time = Date.now() * 0.003;
        for (let i = 0; i < bufferLength; i++) {
          dataArray[i] = Math.max(10, Math.sin(time + i * 0.2) * 20 + 25);
        }
      }

      const barWidth = (canvas.width / bufferLength) * 0.7;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.85;

        const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0);
        if (isPlayingForward) {
          gradient.addColorStop(0, '#10b981');
          gradient.addColorStop(1, '#06b6d4');
        } else if (isPlaying) {
          gradient.addColorStop(0, '#6366f1');
          gradient.addColorStop(1, '#ec4899');
        } else {
          gradient.addColorStop(0, '#334155');
          gradient.addColorStop(1, '#475569');
        }

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x, canvas.height - barHeight - 2, barWidth, barHeight + 2, [3, 3, 0, 0]);
        ctx.fill();

        x += barWidth + (canvas.width / bufferLength) * 0.3;
      }

      animId = requestAnimationFrame(renderWave);
    };

    renderWave();

    return () => {
      cancelAnimationFrame(animId);
    };
  }, [isPlaying, isPlayingForward]);

  const handleVolumeToggle = () => {
    if (isMuted) {
      audioEngine.setVolume(volume);
      setIsMuted(false);
    } else {
      audioEngine.setVolume(0);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    audioEngine.setVolume(val);
    if (val === 0) setIsMuted(true);
    else setIsMuted(false);
  };

  const handleSpeedChange = (newSpeed: number) => {
    setSpeed(newSpeed);
    audioEngine.setPlaybackSpeed(newSpeed);
  };

  return (
    <div className="w-full bg-slate-900/80 backdrop-blur-md border border-slate-800 rounded-2xl p-5 shadow-xl flex flex-col items-center gap-4 transition-all">
      {/* Waveform Canvas */}
      <div className="w-full h-16 bg-slate-950/60 rounded-xl border border-slate-800/80 px-4 py-2 flex items-center justify-center relative overflow-hidden">
        <canvas
          ref={canvasRef}
          width={320}
          height={50}
          className="w-full h-full object-contain"
        />
        {isPlaying && (
          <div className="absolute top-2 right-3 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-semibold animate-pulse border border-indigo-500/30">
            <AudioWaveform className="w-3.5 h-3.5 animate-spin" />
            <span>REVERSE ({speed}x)</span>
          </div>
        )}
        {isPlayingForward && (
          <div className="absolute top-2 right-3 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold animate-pulse border border-emerald-500/30">
            <Sparkles className="w-3.5 h-3.5" />
            <span>ORIGINAL ({speed}x)</span>
          </div>
        )}
      </div>

      {/* Main Play Action Controls */}
      <div className="flex flex-wrap items-center justify-center gap-3 w-full">
        {/* Play Reverse Audio Button */}
        <button
          onClick={onPlayReverse}
          disabled={!isAudioReady || isPlaying}
          className={`flex-1 min-w-[200px] max-w-sm py-3.5 px-6 rounded-xl font-bold text-base flex items-center justify-center gap-3 transition-all transform active:scale-95 shadow-lg ${
            isPlaying
              ? 'bg-indigo-600/60 text-white cursor-not-allowed ring-4 ring-indigo-500/30'
              : 'bg-gradient-to-r from-indigo-600 via-indigo-500 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5'
          }`}
          title="Play phrase backwards (Shortcut: Space)"
        >
          <RotateCcw className={`w-5 h-5 ${isPlaying ? 'animate-spin' : 'group-hover:-rotate-45 transition-transform'}`} />
          <span>{isPlaying ? 'Playing Backwards...' : 'Play Reverse Phrase'}</span>
        </button>

        {/* Play Forward (Normal) Audio Button (Unlocked on win/reveal) */}
        {isWon && (
          <button
            onClick={onPlayForward}
            disabled={isPlayingForward}
            className="py-3.5 px-5 rounded-xl font-bold text-sm bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 flex items-center gap-2 transition-all transform hover:-translate-y-0.5 active:scale-95 animate-pop-in"
            title="Listen to original forward speech"
          >
            <Play className="w-4 h-4 fill-current" />
            <span>Play Original</span>
          </button>
        )}
      </div>

      {/* Speed Slider & Controls */}
      <div className="w-full bg-slate-950/40 rounded-xl p-3 border border-slate-800/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-indigo-300 font-semibold w-full sm:w-auto">
          <Gauge className="w-4 h-4 text-indigo-400 shrink-0" />
          <span>Speed:</span>
          <span className="font-mono bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded-md border border-indigo-500/30 font-bold min-w-[44px] text-center">
            {speed.toFixed(2)}x
          </span>
        </div>

        {/* Speed Slider */}
        <div className="flex items-center gap-3 w-full sm:max-w-xs">
          <span className="text-[11px] text-slate-500 font-medium">0.5x</span>
          <input
            type="range"
            min="0.5"
            max="1.5"
            step="0.05"
            value={speed}
            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
            className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-500"
          />
          <span className="text-[11px] text-slate-500 font-medium">1.5x</span>
        </div>

        {/* Speed Preset Quick Buttons */}
        <div className="flex items-center gap-1 bg-slate-900 p-0.5 rounded-lg border border-slate-800 text-[11px]">
          {[0.5, 0.75, 1.0, 1.25, 1.5].map((preset) => (
            <button
              key={preset}
              onClick={() => handleSpeedChange(preset)}
              className={`px-2 py-0.5 rounded font-bold transition-all ${
                speed === preset
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {preset}x
            </button>
          ))}
        </div>
      </div>

      {/* Replay Counter & Volume Bar */}
      <div className="flex items-center justify-between w-full pt-1 px-1 border-t border-slate-800/80 text-xs text-slate-400">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">Replays:</span>
          <span className="bg-slate-800 text-indigo-300 font-bold px-2 py-0.5 rounded-md border border-slate-700">
            {replaysCount}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleVolumeToggle}
            className="p-1 hover:text-white transition-colors text-slate-400"
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-16 h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};
